import type { Message } from '../types';

/** Chosen branch id per variant group, e.g. `{ 'user-3': 'user-3-b' }`. */
export type ActiveBranchMap = Record<string, string>;

export interface MessageVariantInfo {
  groupId: string;
  /** All variants of this user turn, oldest first. */
  variants: Message[];
  /** 1-based position of the visible variant. */
  index: number;
  total: number;
}

/** A variant's own branch id is the last decision in its path. */
export function ownBranchId(message: Message): string | undefined {
  const path = message.branchPath;
  return path && path.length > 0 ? path[path.length - 1] : undefined;
}

/**
 * Group every variant of each edited user turn, in array order.
 *
 * The message array is append-only, so array order is chronological: the
 * original prompt first, each later edit after it.
 */
export function collectVariantGroups(messages: Message[]): Map<string, Message[]> {
  const groups = new Map<string, Message[]>();
  for (const message of messages) {
    if (message.role !== 'user' || !message.variantGroupId) continue;
    const existing = groups.get(message.variantGroupId);
    if (existing) existing.push(message);
    else groups.set(message.variantGroupId, [message]);
  }
  return groups;
}

/** The variant shown for a group: the user's choice, else the newest. */
function resolveActiveVariant(
  variants: Message[],
  activeBranches: ActiveBranchMap,
  groupId: string,
): Message {
  const chosen = activeBranches[groupId];
  if (chosen) {
    const match = variants.find(variant => ownBranchId(variant) === chosen);
    if (match) return match;
  }
  return variants[variants.length - 1];
}

/** Branch ids currently selected, one per variant group. */
function activeBranchIds(messages: Message[], activeBranches: ActiveBranchMap): Set<string> {
  const selected = new Set<string>();
  for (const [groupId, variants] of collectVariantGroups(messages)) {
    const id = ownBranchId(resolveActiveVariant(variants, activeBranches, groupId));
    if (id) selected.add(id);
  }
  return selected;
}

/**
 * The conversation as it should be read and sent to the model.
 *
 * Storage keeps every branch so nothing is ever lost; this returns only the path
 * the user is currently looking at. A message is on that path when every branch
 * decision above it selects it — messages on abandoned branches, and the
 * programs generated there, stay in storage and come back by switching variants.
 */
export function selectActivePath(messages: Message[], activeBranches: ActiveBranchMap = {}): Message[] {
  const selected = activeBranchIds(messages, activeBranches);
  if (selected.size === 0) return messages;

  return messages.filter(message => {
    const path = message.branchPath;
    if (!path || path.length === 0) return true;
    return path.every(branchId => selected.has(branchId));
  });
}

/** Variant switcher data for a user message, or undefined when it has no siblings. */
export function getVariantInfo(
  message: Message,
  messages: Message[],
  activeBranches: ActiveBranchMap = {},
): MessageVariantInfo | undefined {
  if (message.role !== 'user' || !message.variantGroupId) return undefined;
  const variants = collectVariantGroups(messages).get(message.variantGroupId);
  if (!variants || variants.length < 2) return undefined;

  const active = resolveActiveVariant(variants, activeBranches, message.variantGroupId);
  // Only the visible variant renders the switcher.
  if (active.id !== message.id) return undefined;

  return {
    groupId: message.variantGroupId,
    variants,
    index: variants.indexOf(active) + 1,
    total: variants.length,
  };
}

export interface BranchPreparation {
  /** History with the existing turn tagged onto its own branch. Nothing removed. */
  messages: Message[];
  variantGroupId: string;
  /** Branch the new variant and its replies belong to. */
  newBranchPath: string[];
}

/**
 * Make room for an edited prompt without deleting the turn it replaces.
 *
 * The edited turn and everything after it are tagged onto the branch they
 * already implicitly formed, so the new variant can be appended beside them.
 */
export function prepareBranchForEdit(
  messages: Message[],
  editedMessageId: string,
): BranchPreparation | null {
  const index = messages.findIndex(message => message.id === editedMessageId);
  if (index < 0) return null;

  const edited = messages[index];
  const variantGroupId = edited.variantGroupId || edited.id;
  const existingPath = edited.branchPath || [];
  const alreadyBranched = Boolean(edited.variantGroupId) && existingPath.length > 0;

  // Siblings share everything above the decision point.
  const parentPath = alreadyBranched ? existingPath.slice(0, -1) : existingPath;
  const siblingCount = collectVariantGroups(messages).get(variantGroupId)?.length || 1;
  const newBranchId = `${variantGroupId}-v${siblingCount + 1}`;

  const next = alreadyBranched
    ? messages
    : messages.map((message, position) => {
      if (position < index) return message;
      const originalBranchId = `${variantGroupId}-v1`;
      const base = message.branchPath || [];
      const tagged = { ...message, branchPath: [...base, originalBranchId] };
      return position === index ? { ...tagged, variantGroupId } : tagged;
    });

  return { messages: next, variantGroupId, newBranchPath: [...parentPath, newBranchId] };
}
